import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { 
  User, 
  Lock, 
  Settings as SettingsIcon, 
  Bell,
  Moon,
  Globe,
  Shield,
  Users as UsersIcon,
  UserPlus,
  CheckCircle,
  XCircle,
  Clock,
  Percent,
  Trash2,
  Building2
} from 'lucide-react';
import StaffelManagement from '../components/StaffelManagement';
import CompanySettings from '../components/CompanySettings';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const Settings = () => {
  const { user, updateUser, logout, token } = useAuth();
  const { toast } = useToast();
  
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingPending, setLoadingPending] = useState(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    if (user?.role === 'owner') {
      fetchUsers();
      fetchPendingUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await axios.get(`${API}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
      setLoadingUsers(false);
    } catch (error) {
      console.error('Fout bij ophalen gebruikers:', error);
      setLoadingUsers(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      setLoadingPending(true);
      const response = await axios.get(`${API}/auth/pending-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingUsers(response.data);
      setLoadingPending(false);
    } catch (error) {
      console.error('Fout bij ophalen wachtende gebruikers:', error);
      setLoadingPending(false);
    }
  };

  const approveUser = async (userId) => {
    try {
      await axios.post(`${API}/auth/approve/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Gelukt",
        description: "Gebruiker is goedgekeurd en kan nu inloggen"
      });
      fetchPendingUsers();
      fetchUsers();
    } catch (error) {
      toast({
        title: "Fout",
        description: "Kan gebruiker niet goedkeuren",
        variant: "destructive"
      });
    }
  };

  const rejectUser = async (userId) => {
    try {
      await axios.post(`${API}/auth/reject/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Afgewezen",
        description: "Gebruikersregistratie is afgewezen"
      });
      fetchPendingUsers();
      fetchUsers();
    } catch (error) {
      toast({
        title: "Fout",
        description: "Kan gebruiker niet afwijzen",
        variant: "destructive"
      });
    }
  };

  const updateUserPermissions = async (userId, permissions) => {
    try {
      await axios.put(`${API}/auth/users/${userId}`, 
        { permissions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: "Gelukt",
        description: "Gebruikersrechten succesvol bijgewerkt"
      });
      fetchUsers();
    } catch (error) {
      toast({
        title: "Fout",
        description: "Kan rechten niet bijwerken",
        variant: "destructive"
      });
    }
  };

  const toggleUserStatus = async (userId, isActive) => {
    try {
      await axios.put(`${API}/auth/users/${userId}`, 
        { isActive: !isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: "Gelukt",
        description: `Gebruiker ${!isActive ? 'geactiveerd' : 'gedeactiveerd'}`
      });
      fetchUsers();
    } catch (error) {
      toast({
        title: "Fout",
        description: "Kan gebruikersstatus niet bijwerken",
        variant: "destructive"
      });
    }
  };

  const deleteUser = async (userId, userName) => {
    try {
      await axios.delete(`${API}/auth/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Gelukt",
        description: `Gebruiker ${userName} is verwijderd`
      });
      fetchUsers();
    } catch (error) {
      toast({
        title: "Fout",
        description: error.response?.data?.detail || "Kan gebruiker niet verwijderen",
        variant: "destructive"
      });
    }
  };

  const getPermissionLabel = (permission) => {
    const labels = {
      'read-only': 'Alleen Lezen',
      'read': 'Alleen Lezen',
      'create': 'Aanmaken',
      'create-edit': 'Aanmaken & Bewerken',
      'full': 'Volledige Toegang'
    };
    return labels[permission] || permission;
  };

  const getPermissionDescription = (permission) => {
    const descriptions = {
      'read-only': 'Kan alleen gegevens bekijken, geen aanmaken/bewerken/verwijderen',
      'read': 'Kan alleen gegevens bekijken, geen aanmaken/bewerken/verwijderen',
      'create': 'Kan projecten aanmaken maar niet bewerken',
      'create-edit': 'Kan projecten aanmaken en bewerken, apparatuur toevoegen',
      'full': 'Volledige medewerker toegang - aanmaken, bewerken, verwijderen'
    };
    return descriptions[permission] || '';
  };
  
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [appSettings, setAppSettings] = useState({
    notifications: true,
    emailNotifications: false,
    darkMode: false,
    language: 'nl'
  });

  const handleProfileUpdate = (e) => {
    e.preventDefault();
    updateUser({ name: profile.name, email: profile.email });
    toast({
      title: "Gelukt",
      description: "Profiel succesvol bijgewerkt"
    });
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: "Fout",
        description: "Wachtwoorden komen niet overeen",
        variant: "destructive"
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast({
        title: "Fout",
        description: "Wachtwoord moet minimaal 6 tekens bevatten",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Gelukt",
      description: "Wachtwoord succesvol gewijzigd"
    });
    
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleLogout = () => {
    logout();
    toast({
      title: "Uitgelogd",
      description: "U bent succesvol uitgelogd"
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Instellingen</h1>
          <p className="text-gray-500 mt-1">Beheer uw account en applicatie voorkeuren</p>
        </div>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className={`grid w-full ${user?.role === 'owner' ? 'grid-cols-6 lg:w-[750px]' : 'grid-cols-3 lg:w-[400px]'}`}>
          <TabsTrigger value="profile">Profiel</TabsTrigger>
          <TabsTrigger value="security">Beveiliging</TabsTrigger>
          <TabsTrigger value="preferences">Voorkeuren</TabsTrigger>
          {user?.role === 'owner' && (
            <>
              <TabsTrigger value="company">Bedrijf</TabsTrigger>
              <TabsTrigger value="staffels">Staffels</TabsTrigger>
              <TabsTrigger value="access">Toegang</TabsTrigger>
            </>
          )}
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="shadow-lg border-0">
              <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-purple-50">
                <CardTitle className="flex items-center text-lg">
                  <User className="w-5 h-5 mr-2 text-blue-600" />
                  Profiel Informatie
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Volledige Naam</Label>
                    <Input
                      id="name"
                      value={profile.name}
                      onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                      placeholder="Jan Jansen"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-mailadres</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                      placeholder="jan@voorbeeld.nl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefoonnummer</Label>
                    <Input
                      id="phone"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      placeholder="+31 6 12345678"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-purple-600">
                    Profiel Opslaan
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="shadow-lg border-0">
              <CardHeader className="border-b bg-gradient-to-r from-green-50 to-blue-50">
                <CardTitle className="flex items-center text-lg">
                  <SettingsIcon className="w-5 h-5 mr-2 text-green-600" />
                  Account Informatie
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Account Type</p>
                      <p className="text-sm text-gray-500">{user?.role === 'owner' ? 'Eigenaar' : 'Medewerker'}</p>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                      {user?.role === 'owner' ? 'Eigenaar' : 'Medewerker'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Rechten</p>
                      <p className="text-sm text-gray-500">{getPermissionDescription(user?.permissions || 'full')}</p>
                    </div>
                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                      {getPermissionLabel(user?.permissions || 'full')}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Status</p>
                      <p className="text-sm text-gray-500">Uw account is actief</p>
                    </div>
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Actief</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="security" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="shadow-lg border-0">
              <CardHeader className="border-b bg-gradient-to-r from-red-50 to-yellow-50">
                <CardTitle className="flex items-center text-lg">
                  <Lock className="w-5 h-5 mr-2 text-red-600" />
                  Wachtwoord Wijzigen
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Huidig Wachtwoord</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      placeholder="••••••••"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">Nieuw Wachtwoord</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      placeholder="••••••••"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Bevestig Nieuw Wachtwoord</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-red-600 to-yellow-600">
                    Wachtwoord Wijzigen
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="shadow-lg border-0">
              <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-red-50">
                <CardTitle className="flex items-center text-lg">
                  <Shield className="w-5 h-5 mr-2 text-purple-600" />
                  Sessie Beheer
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="font-medium text-gray-900">Huidige Sessie</span>
                      </div>
                      <span className="text-sm text-gray-500">Actief</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Ingelogd als {user?.email}
                    </p>
                  </div>
                  <Button 
                    onClick={handleLogout}
                    variant="outline" 
                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                  >
                    Uitloggen van Alle Apparaten
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="preferences" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="shadow-lg border-0">
              <CardHeader className="border-b bg-gradient-to-r from-yellow-50 to-green-50">
                <CardTitle className="flex items-center text-lg">
                  <Bell className="w-5 h-5 mr-2 text-yellow-600" />
                  Meldingen
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Push Meldingen</p>
                      <p className="text-sm text-gray-500">Ontvang meldingen voor updates</p>
                    </div>
                    <Switch
                      checked={appSettings.notifications}
                      onCheckedChange={(checked) => setAppSettings({ ...appSettings, notifications: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">E-mail Meldingen</p>
                      <p className="text-sm text-gray-500">Ontvang dagelijkse e-mail samenvattingen</p>
                    </div>
                    <Switch
                      checked={appSettings.emailNotifications}
                      onCheckedChange={(checked) => setAppSettings({ ...appSettings, emailNotifications: checked })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg border-0">
              <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-purple-50">
                <CardTitle className="flex items-center text-lg">
                  <Globe className="w-5 h-5 mr-2 text-blue-600" />
                  Weergave
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Moon className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="font-medium text-gray-900">Donkere Modus</p>
                        <p className="text-sm text-gray-500">Gebruik donker thema</p>
                      </div>
                    </div>
                    <Switch
                      checked={appSettings.darkMode}
                      onCheckedChange={(checked) => setAppSettings({ ...appSettings, darkMode: checked })}
                    />
                  </div>
                  <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Globe className="w-5 h-5 text-gray-600" />
                      <p className="font-medium text-gray-900">Taal</p>
                    </div>
                    <Select 
                      value={appSettings.language} 
                      onValueChange={(value) => setAppSettings({ ...appSettings, language: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nl">Nederlands</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="de">Deutsch</SelectItem>
                        <SelectItem value="fr">Français</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {user?.role === 'owner' && (
          <TabsContent value="company" className="mt-6">
            <CompanySettings />
          </TabsContent>
        )}

        {user?.role === 'owner' && (
          <TabsContent value="staffels" className="mt-6">
            <Card className="shadow-lg border-0">
              <CardHeader className="border-b bg-gradient-to-r from-green-50 to-blue-50">
                <CardTitle className="flex items-center text-lg">
                  <Percent className="w-5 h-5 mr-2 text-green-600" />
                  Staffel Beheer
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <StaffelManagement />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {user?.role === 'owner' && (
          <TabsContent value="access" className="mt-6">
            <div className="space-y-6">
              {pendingUsers.length > 0 && (
                <Card className="shadow-lg border-0 border-l-4 border-l-yellow-500">
                  <CardHeader className="border-b bg-gradient-to-r from-yellow-50 to-orange-50">
                    <CardTitle className="flex items-center text-lg">
                      <Clock className="w-5 h-5 mr-2 text-yellow-600" />
                      Wachtende Goedkeuringen ({pendingUsers.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      {loadingPending ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mx-auto"></div>
                        </div>
                      ) : (
                        pendingUsers.map((pendingUser) => (
                          <div key={pendingUser.id} className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                            <div className="flex items-center space-x-4">
                              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                                <UserPlus className="w-5 h-5 text-yellow-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{pendingUser.name}</p>
                                <p className="text-sm text-gray-500">{pendingUser.email}</p>
                                <div className="flex items-center mt-1 space-x-2">
                                  {pendingUser.emailVerified ? (
                                    <Badge className="bg-green-100 text-green-700 text-xs">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      E-mail geverifieerd
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-red-100 text-red-700 text-xs">
                                      <Clock className="w-3 h-3 mr-1" />
                                      E-mail niet geverifieerd
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                onClick={() => approveUser(pendingUser.id)}
                                className="bg-green-600 hover:bg-green-700"
                                disabled={!pendingUser.emailVerified}
                                title={!pendingUser.emailVerified ? "Gebruiker moet eerst e-mail verifiëren" : ""}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Goedkeuren
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => rejectUser(pendingUser.id)}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Afwijzen
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="shadow-lg border-0">
                <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-blue-50">
                  <CardTitle className="flex items-center text-lg">
                    <UsersIcon className="w-5 h-5 mr-2 text-purple-600" />
                    Gebruikersbeheer
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {loadingUsers ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                    </div>
                  ) : users.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Geen gebruikers gevonden</p>
                  ) : (
                    <div className="space-y-4">
                      {users.map((u) => (
                        <div key={u.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              u.role === 'owner' ? 'bg-purple-100' : 'bg-blue-100'
                            }`}>
                              <User className={`w-5 h-5 ${u.role === 'owner' ? 'text-purple-600' : 'text-blue-600'}`} />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{u.name}</p>
                              <p className="text-sm text-gray-500">{u.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <Badge className={u.role === 'owner' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                              {u.role === 'owner' ? 'Eigenaar' : 'Medewerker'}
                            </Badge>
                            {u.role !== 'owner' && (
                              <>
                                <Select
                                  value={u.permissions || 'read-only'}
                                  onValueChange={(value) => updateUserPermissions(u.id, value)}
                                >
                                  <SelectTrigger className="w-[180px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="read-only">Alleen Lezen</SelectItem>
                                    <SelectItem value="create">Aanmaken</SelectItem>
                                    <SelectItem value="create-edit">Aanmaken & Bewerken</SelectItem>
                                    <SelectItem value="full">Volledige Toegang</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Switch
                                  checked={u.isActive !== false}
                                  onCheckedChange={() => toggleUserStatus(u.id, u.isActive !== false)}
                                />
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-red-600 hover:bg-red-50 border-red-200"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Gebruiker Verwijderen</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Weet je zeker dat je {u.name} wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt. De gebruiker wordt ook uit de personeelslijst verwijderd.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => deleteUser(u.id, u.name)}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        Verwijderen
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Settings;
